import { AxiosError } from "axios";
import { axiosInstance } from "../../Authentication/AxiosInterceptor";

export type ClientListItem = {
  id: string;
  name: string;
};

export type QueuedAuth0User = {
  sub: string;
  email: string;
  cosmosUserId: string;
};

export type ContainerActionCount = {
  container: string;
  deleted: number;
  patched: number;
  failed: number;
};

export type ClientHardDeleteReport = {
  clientId: string;
  clientFound: boolean;
  blobsDeleted: number;
  blobsFailed: number;
  failedPhase?: string | null;
  error?: string | null;
  containers: ContainerActionCount[];
  queuedAuth0Users: QueuedAuth0User[];
};

export type PendingAuth0Delete = {
  id: string;
  clientId: string;
  sub: string;
  email: string;
  cosmosUserId: string;
  queuedAt?: string;
};

export type PendingAuth0DeleteFailure = {
  user: PendingAuth0Delete;
  error: string;
};

export type PendingAuth0DeleteReport = {
  clientId: string;
  deleted: PendingAuth0Delete[];
  alreadyAbsent: PendingAuth0Delete[];
  failed: PendingAuth0DeleteFailure[];
};

const text = (value: unknown): string => (value == null ? "" : String(value));

const field = (source: Record<string, unknown>, camel: string, pascal: string): unknown =>
  source[camel] ?? source[pascal];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asList = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.map(asRecord) : [];

const queuedUser = (value: unknown): QueuedAuth0User => {
  const source = asRecord(value);
  return {
    sub: text(field(source, "sub", "Sub")),
    email: text(field(source, "email", "Email")),
    cosmosUserId: text(field(source, "cosmosUserId", "CosmosUserId")),
  };
};

const pendingUser = (value: unknown): PendingAuth0Delete => {
  const source = asRecord(value);
  return {
    id: text(field(source, "id", "Id")),
    clientId: text(field(source, "clientId", "ClientId")),
    sub: text(field(source, "sub", "Sub")),
    email: text(field(source, "email", "Email")),
    cosmosUserId: text(field(source, "cosmosUserId", "CosmosUserId")),
    queuedAt: text(field(source, "queuedAt", "QueuedAt")),
  };
};

export const getClientList = async (): Promise<ClientListItem[]> => {
  const response = await axiosInstance.get("GetClientList");
  return asList(response.data)
    .map((client) => ({
      id: text(field(client, "id", "Id")),
      name: text(field(client, "name", "Name")),
    }))
    .filter((client) => client.id.length > 0);
};

export const hardDeleteClient = async (clientId: string): Promise<ClientHardDeleteReport> => {
  const response = await axiosInstance.post(`HardDeleteClient/${encodeURIComponent(clientId)}`);
  return normalizeWipeReport(response.data);
};

export const listPendingAuth0Deletes = async (clientId: string): Promise<PendingAuth0Delete[]> => {
  const response = await axiosInstance.get(`Auth0/PendingDeletes/${encodeURIComponent(clientId)}`);
  return asList(response.data).map(pendingUser);
};

export const deletePendingAuth0Users = async (clientId: string): Promise<PendingAuth0DeleteReport> => {
  const response = await axiosInstance.post(`Auth0/DeletePendingUsers/${encodeURIComponent(clientId)}`);
  return normalizeAuth0Report(response.data);
};

export const wipeReportFromError = (error: unknown): ClientHardDeleteReport | null => {
  const data = (error as AxiosError)?.response?.data;
  if (!data || typeof data !== "object") return null;
  const source = asRecord(data);
  if (field(source, "failedPhase", "FailedPhase") == null && field(source, "queuedAuth0Users", "QueuedAuth0Users") == null) {
    return null;
  }
  return normalizeWipeReport(data);
};

const normalizeWipeReport = (value: unknown): ClientHardDeleteReport => {
  const source = asRecord(value);
  return {
    clientId: text(field(source, "clientId", "ClientId")),
    clientFound: Boolean(field(source, "clientFound", "ClientFound")),
    blobsDeleted: Number(field(source, "blobsDeleted", "BlobsDeleted") ?? 0),
    blobsFailed: Number(field(source, "blobsFailed", "BlobsFailed") ?? 0),
    failedPhase: text(field(source, "failedPhase", "FailedPhase")) || null,
    error: text(field(source, "error", "Error")) || null,
    containers: asList(field(source, "containers", "Containers")).map((container) => ({
      container: text(field(container, "container", "Container")),
      deleted: Number(field(container, "deleted", "Deleted") ?? 0),
      patched: Number(field(container, "patched", "Patched") ?? 0),
      failed: Number(field(container, "failed", "Failed") ?? 0),
    })),
    queuedAuth0Users: asList(field(source, "queuedAuth0Users", "QueuedAuth0Users")).map(queuedUser),
  };
};

const normalizeAuth0Report = (value: unknown): PendingAuth0DeleteReport => {
  const source = asRecord(value);
  return {
    clientId: text(field(source, "clientId", "ClientId")),
    deleted: asList(field(source, "deleted", "Deleted")).map(pendingUser),
    alreadyAbsent: asList(field(source, "alreadyAbsent", "AlreadyAbsent")).map(pendingUser),
    failed: asList(field(source, "failed", "Failed")).map((failure) => ({
      user: pendingUser(field(failure, "user", "User")),
      error: text(field(failure, "error", "Error")),
    })),
  };
};
